import React from "react";
import { useForm } from "react-hook-form";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { Layout } from "views/common/Layout";
import { BenefitsSection } from "views/components/BenefitsSection";
import { FAQSection } from "views/components/FAQSection";
import {
    Form,
    FormField,
    FormItem,
    FormLabel,
    FormControl,
    FormDescription,
    FormMessage,
} from "views/components/ui/Form";
import { loadStripe } from "@stripe/stripe-js";
import { createCheckoutSession } from "actions/payment";

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const Submit = ({ userAuth, createCheckoutSession }) => {
    const form = useForm({
        defaultValues: {
            name: "",
            description: "",
            website: "",
            category: "",
            pricing: "",
            features: "",
            thumbnail: "",
            email: "",
        },
        mode: "onSubmit",
    });

    const handleSubmit = (data) => {
        // Include user UUID if logged in
        const checkoutData = {
            ...data,
            userUuid: userAuth?.uuid,
        };
        createCheckoutSession(checkoutData);
    };

    return (
        <Layout>
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
                <div className="mx-auto max-w-3xl">
                    <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                        Submit Your Tool
                    </h1>
                    <p className="mt-4 text-lg text-gray-600">
                        Share your tool with our community. Fill out the form
                        below and we'll review your submission.
                    </p>
                </div>

                <div className="mt-8">
                    <BenefitsSection />
                </div>

                <div className="mx-auto max-w-3xl mt-12">
                    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8">
                        <div className="text-center mb-8">
                            <h2 className="text-2xl font-bold text-gray-900">
                                Tool Submission Form
                            </h2>
                        </div>

                        <Form {...form}>
                            <form
                                onSubmit={form.handleSubmit(handleSubmit)}
                                className="space-y-8"
                            >
                                <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                                    <FormField
                                        control={form.control}
                                        name="name"
                                        rules={{
                                            required: "Tool name is required",
                                        }}
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel className="text-base font-medium text-gray-900">
                                                    Tool Name
                                                </FormLabel>
                                                <FormControl>
                                                    <input
                                                        {...field}
                                                        type="text"
                                                        className={`mt-2 block w-full rounded-lg border ${
                                                            form.formState
                                                                .errors.name
                                                                ? "border-red-300 focus:border-red-500"
                                                                : "border-gray-300 focus:border-blue-500"
                                                        } px-4 py-3 text-base shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm transition-colors duration-200`}
                                                        placeholder="Enter your tool's name"
                                                    />
                                                </FormControl>
                                                <FormMessage className="text-sm text-red-600 mt-2 font-medium" />
                                                <FormDescription className="text-sm text-gray-500 mt-2">
                                                    The name of your tool as it
                                                    will appear on our platform.
                                                </FormDescription>
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="website"
                                        rules={{
                                            required: "Website URL is required",
                                            pattern: {
                                                value: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/,
                                                message:
                                                    "Please enter a valid URL",
                                            },
                                        }}
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel className="text-base font-medium text-gray-900">
                                                    Website URL
                                                </FormLabel>
                                                <FormControl>
                                                    <input
                                                        {...field}
                                                        type="url"
                                                        className={`mt-2 block w-full rounded-lg border ${
                                                            form.formState
                                                                .errors.website
                                                                ? "border-red-300 focus:border-red-500"
                                                                : "border-gray-300 focus:border-blue-500"
                                                        } px-4 py-3 text-base shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm transition-colors duration-200`}
                                                        placeholder="https://your-tool-website.com"
                                                    />
                                                </FormControl>
                                                <FormMessage className="text-sm text-red-600 mt-2 font-medium" />
                                                <FormDescription className="text-sm text-gray-500 mt-2">
                                                    The URL where users can find
                                                    your tool
                                                </FormDescription>
                                            </FormItem>
                                        )}
                                    />
                                </div>

                                <div className="pt-6">
                                    <button
                                        type="submit"
                                        disabled={form.formState.isSubmitting}
                                        className="w-full rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {form.formState.isSubmitting
                                            ? "Processing..."
                                            : "Submit Tool ($29.00)"}
                                    </button>
                                </div>
                            </form>
                        </Form>
                    </div>
                </div>

                <div className="mx-auto max-w-3xl mt-16">
                    <FAQSection />
                </div>
            </div>
        </Layout>
    );
};

const mapStateToProps = (state) => ({
    userAuth: state.auth?.userAuth,
});

export default connect(mapStateToProps, {
    createCheckoutSession,
})(withRouter(Submit));
